import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { MultiSelect, type SelectOption } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { RowAvatar } from "@/components/domain/row-avatar"
import {
  DEFAULTABLE_ROLES,
  type AgentPool,
  type AgentPoolMember,
  type AgentPoolsIndexProps,
  type DefaultableRole,
} from "@/types/agent_pool"

const ROLE_LABELS: Record<DefaultableRole, string> = {
  admin: "Admin",
  agent_manager: "Manager",
  agent: "Agent",
}

// Single-page workspace for AgentPool CRUD. Support pools are named groups of
// CompanyUsers referenced by EscalationPolicy.assignee_agent_pool_id; the
// policy editor picks a pool by name, the matcher dereferences members at
// match time. All mutations are Inertia router calls (CSRF + flash +
// page refresh handled).

const BASE = "/agent_pools"
const DRAFT_ID = -1
const COL_COUNT = 5

type EditorState = {
  name: string
  description: string
  defaultForRoles: DefaultableRole[]
  memberIds: number[]
}

function emptyEditor(): EditorState {
  return { name: "", description: "", defaultForRoles: [], memberIds: [] }
}

function fromPool(pool: AgentPool): EditorState {
  return {
    name: pool.name,
    description: pool.description ?? "",
    defaultForRoles: [...pool.defaultForRoles],
    memberIds: [...pool.memberIds],
  }
}

export default function AgentPoolsIndex({ pools, members, poolCapabilities }: AgentPoolsIndexProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [editor, setEditor] = useState<EditorState>(emptyEditor())
  const [submitting, setSubmitting] = useState(false)

  const memberIndex = useMemo(() => {
    const m = new Map<number, AgentPoolMember>()
    members.forEach((mem) => m.set(mem.companyUserId, mem))
    return m
  }, [members])

  const memberOptions: SelectOption<number>[] = useMemo(
    () => members.map((m) => ({ value: m.companyUserId, label: `${m.name} (${m.email})` })),
    [members],
  )

  const startNew = () => {
    setExpandedId(DRAFT_ID)
    setEditor(emptyEditor())
  }

  const startEdit = (pool: AgentPool) => {
    setExpandedId(pool.id)
    setEditor(fromPool(pool))
  }

  const cancel = () => {
    setExpandedId(null)
    setEditor(emptyEditor())
  }

  const save = () => {
    if (!editor.name.trim()) return
    setSubmitting(true)
    const body = {
      agent_pool: {
        name: editor.name.trim(),
        description: editor.description.trim(),
        default_for_roles: editor.defaultForRoles,
        company_user_ids: editor.memberIds,
      },
    }
    const onFinish = () => {
      setSubmitting(false)
      setExpandedId(null)
      setEditor(emptyEditor())
    }
    if (expandedId === DRAFT_ID) {
      router.post(BASE, body, { preserveScroll: true, onFinish })
    } else if (expandedId !== null) {
      router.patch(`${BASE}/${expandedId}`, body, { preserveScroll: true, onFinish })
    }
  }

  const remove = (pool: AgentPool) => {
    if (!window.confirm(`Delete pool "${pool.name}"?`)) return
    router.delete(`${BASE}/${pool.id}`, { preserveScroll: true })
  }

  const canEdit = poolCapabilities.canEdit
  const showEmpty = pools.length === 0 && expandedId !== DRAFT_ID

  return (
    <AppShell>
      <PageHeader
        eyebrow="Escalation"
        title="Support Pools"
        subtitle="Named groups of human operators referenced by triage rules."
        actions={canEdit ? <Button onClick={startNew} className="w-full sm:w-auto">+ New support pool</Button> : undefined}
      />

      {showEmpty ? (
        <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
          No support pools yet. Define a group of operators here so triage rules can reference them by name.
        </div>
      ) : (
        <>
          {/* Desktop: full table. */}
          <div className="hidden overflow-x-auto rounded-card border border-border bg-app-panel lg:block">
            <table className="w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-white/[0.02] text-left">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-app-muted">Name</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-app-muted">Defaults</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-app-muted">Members</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-app-muted">Used by</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-app-muted w-10" />
                </tr>
              </thead>
              <tbody>
                {expandedId === DRAFT_ID && (
                  <tr className="border-b border-border/50 bg-brand-red/[0.04]">
                    <td colSpan={COL_COUNT} className="px-4 py-4">
                      <PoolEditor
                        draft
                        editor={editor}
                        memberOptions={memberOptions}
                        memberIndex={memberIndex}
                        onChange={setEditor}
                        onCancel={cancel}
                        onSave={save}
                        submitting={submitting}
                      />
                    </td>
                  </tr>
                )}

                {pools.map((pool) => {
                  const expanded = expandedId === pool.id
                  return (
                    <PoolRows
                      key={pool.id}
                      pool={pool}
                      memberIndex={memberIndex}
                      canEdit={canEdit}
                      expanded={expanded}
                      onToggle={() => (expanded ? cancel() : startEdit(pool))}
                      onDelete={() => remove(pool)}
                      editor={editor}
                      memberOptions={memberOptions}
                      onEditorChange={setEditor}
                      onSave={save}
                      submitting={submitting}
                    />
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile / tablet: card list. */}
          <ul className="space-y-3 lg:hidden">
            {expandedId === DRAFT_ID && (
              <li className="rounded-card border border-brand-red/40 bg-brand-red/[0.04] p-4">
                <PoolEditor
                  draft
                  editor={editor}
                  memberOptions={memberOptions}
                  memberIndex={memberIndex}
                  onChange={setEditor}
                  onCancel={cancel}
                  onSave={save}
                  submitting={submitting}
                />
              </li>
            )}
            {pools.map((pool) => {
              const expanded = expandedId === pool.id
              return (
                <li key={pool.id} className="rounded-card border border-border bg-app-panel p-4">
                  <PoolCard
                    pool={pool}
                    memberIndex={memberIndex}
                    canEdit={canEdit}
                    expanded={expanded}
                    onToggle={() => (expanded ? cancel() : startEdit(pool))}
                    onDelete={() => remove(pool)}
                  />
                  {expanded && (
                    <div className="mt-4 border-t border-border pt-4">
                      <PoolEditor
                        editor={editor}
                        memberOptions={memberOptions}
                        memberIndex={memberIndex}
                        onChange={setEditor}
                        onCancel={cancel}
                        onSave={save}
                        submitting={submitting}
                      />
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </AppShell>
  )
}

// Mobile card surface. Same data the table row renders, stacked vertically.
function PoolCard({
  pool,
  memberIndex,
  canEdit,
  expanded,
  onToggle,
  onDelete,
}: {
  pool: AgentPool
  memberIndex: Map<number, AgentPoolMember>
  canEdit: boolean
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  const stack = pool.memberIds.slice(0, 3)
  const overflow = Math.max(0, pool.memberCount - stack.length)

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-app-text">{pool.name}</div>
          {pool.description && (
            <div className="mt-0.5 line-clamp-2 text-xs text-app-muted">{pool.description}</div>
          )}
        </div>
        {canEdit && (
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="ghost" onClick={onToggle}>
              {expanded ? "Close" : "Edit"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              className="text-status-error hover:bg-status-error/10"
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {pool.defaultForRoles.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {pool.defaultForRoles.map((r) => (
            <Badge key={r} tone="success">Default · {ROLE_LABELS[r]}</Badge>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <div className="flex -space-x-2">
          {stack.map((cuId) => {
            const m = memberIndex.get(cuId)
            const name = m?.name ?? "?"
            return (
              <RowAvatar
                key={cuId}
                name={name}
                src={m?.avatarUrl ?? null}
                className="h-6 w-6 ring-2 ring-app-panel"
              />
            )
          })}
          {overflow > 0 && (
            <div className="flex h-6 w-6 items-center justify-center rounded-control bg-app-panel2 text-[10px] font-medium text-app-muted ring-2 ring-app-panel">
              +{overflow}
            </div>
          )}
        </div>
        <span className="whitespace-nowrap text-xs text-app-muted">
          {pool.memberCount} member{pool.memberCount === 1 ? "" : "s"}
        </span>
        <span className="ml-auto whitespace-nowrap text-xs text-app-muted">
          {pool.usedByPolicyCount} {pool.usedByPolicyCount === 1 ? "policy" : "policies"}
        </span>
      </div>
    </div>
  )
}

type PoolRowsProps = {
  pool: AgentPool
  memberIndex: Map<number, AgentPoolMember>
  canEdit: boolean
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  editor: EditorState
  memberOptions: SelectOption<number>[]
  onEditorChange: (next: EditorState) => void
  onSave: () => void
  submitting: boolean
}

function PoolRows({
  pool,
  memberIndex,
  canEdit,
  expanded,
  onToggle,
  onDelete,
  editor,
  memberOptions,
  onEditorChange,
  onSave,
  submitting,
}: PoolRowsProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const stack = pool.memberIds.slice(0, 3)
  const overflow = Math.max(0, pool.memberCount - stack.length)

  return (
    <>
      <tr
        onClick={() => canEdit && onToggle()}
        className={`border-b border-border/50 align-top transition ${canEdit ? "cursor-pointer hover:bg-white/[0.04]" : ""} ${expanded ? "bg-white/[0.04]" : ""}`}
      >
        <td className="px-4 py-3">
          <div className="font-medium text-app-text">{pool.name}</div>
          {pool.description && (
            <div className="mt-0.5 line-clamp-2 max-w-[320px] text-xs text-app-muted">{pool.description}</div>
          )}
        </td>

        <td className="px-4 py-3">
          {pool.defaultForRoles.length === 0 ? (
            <span className="text-app-subtle">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {pool.defaultForRoles.map((r) => (
                <Badge key={r} tone="success">Default · {ROLE_LABELS[r]}</Badge>
              ))}
            </div>
          )}
        </td>

        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {stack.map((cuId) => {
                const m = memberIndex.get(cuId)
                const name = m?.name ?? "?"
                return (
                  <RowAvatar
                    key={cuId}
                    name={name}
                    src={m?.avatarUrl ?? null}
                    className="h-6 w-6 ring-2 ring-app-panel"
                  />
                )
              })}
              {overflow > 0 && (
                <div className="flex h-6 w-6 items-center justify-center rounded-control bg-app-panel2 text-[10px] font-medium text-app-muted ring-2 ring-app-panel">
                  +{overflow}
                </div>
              )}
            </div>
            <span className="whitespace-nowrap text-xs text-app-muted">
              {pool.memberCount} member{pool.memberCount === 1 ? "" : "s"}
            </span>
          </div>
        </td>

        <td className="px-4 py-3">
          <span className="whitespace-nowrap text-xs text-app-muted">
            {pool.usedByPolicyCount} {pool.usedByPolicyCount === 1 ? "policy" : "policies"}
          </span>
        </td>

        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="rounded-control border border-border px-2 py-1 text-app-muted transition hover:bg-white/5"
                aria-label="Row actions"
              >
                …
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-8 z-10 flex w-32 flex-col rounded-control border border-border bg-app-panel2 shadow-card"
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onToggle() }}
                    className="px-3 py-2 text-left text-xs text-app-text transition hover:bg-white/5"
                  >
                    {expanded ? "Close" : "Edit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onDelete() }}
                    className="border-t border-border px-3 py-2 text-left text-xs text-status-error transition hover:bg-status-error/10"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-border/50 bg-white/[0.02]">
          <td colSpan={COL_COUNT} className="px-4 py-4">
            <PoolEditor
              editor={editor}
              memberOptions={memberOptions}
              memberIndex={memberIndex}
              onChange={onEditorChange}
              onCancel={onToggle}
              onSave={onSave}
              submitting={submitting}
            />
          </td>
        </tr>
      )}
    </>
  )
}

type EditorProps = {
  draft?: boolean
  editor: EditorState
  memberOptions: SelectOption<number>[]
  memberIndex: Map<number, AgentPoolMember>
  onChange: (next: EditorState) => void
  onCancel: () => void
  onSave: () => void
  submitting: boolean
}

function PoolEditor({ draft, editor, memberOptions, memberIndex, onChange, onCancel, onSave, submitting }: EditorProps) {
  const selected: SelectOption<number>[] = editor.memberIds
    .map((id) => memberOptions.find((o) => o.value === id))
    .filter((o): o is SelectOption<number> => Boolean(o))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-wide text-app-muted">Name</label>
        <Input
          value={editor.name}
          onChange={(e) => onChange({ ...editor, name: e.target.value })}
          placeholder="VIP responders"
          maxLength={80}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-wide text-app-muted">Description</label>
        <Textarea
          value={editor.description}
          onChange={(e) => onChange({ ...editor, description: e.target.value })}
          placeholder="Senior teammates who own VIP escalations."
          rows={2}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-wide text-app-muted">Members</label>
        <MultiSelect<number>
          value={selected}
          options={memberOptions}
          placeholder="Add operators…"
          onChange={(next) => {
            const arr = (next ?? []) as readonly SelectOption<number>[]
            onChange({ ...editor, memberIds: arr.map((o) => o.value) })
          }}
        />
        {editor.memberIds.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {editor.memberIds.map((id) => {
              const m = memberIndex.get(id)
              return (
                <Badge key={id} tone="neutral">
                  {m?.name ?? `#${id}`}
                </Badge>
              )
            })}
          </div>
        )}
      </div>
      <div className="rounded-control border border-border bg-app-panel2/40 px-3 py-2 text-sm text-app-text">
        <div className="font-medium">Default pool for…</div>
        <div className="mt-0.5 text-xs text-app-muted">
          New teammates invited at any role you check below are auto-added to this pool. The first time you check
          a role and save, current teammates of that role are bulk-added in one shot.
        </div>
        <div className="mt-2 flex flex-wrap gap-3">
          {DEFAULTABLE_ROLES.map((role) => {
            const checked = editor.defaultForRoles.includes(role)
            return (
              <label key={role} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...editor.defaultForRoles, role]
                      : editor.defaultForRoles.filter((r) => r !== role)
                    onChange({ ...editor, defaultForRoles: next })
                  }}
                  className="h-4 w-4 accent-brand-red"
                />
                <span>{ROLE_LABELS[role]}</span>
              </label>
            )
          })}
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button onClick={onSave} disabled={submitting || !editor.name.trim()}>
          {submitting ? "Saving…" : draft ? "Create pool" : "Save changes"}
        </Button>
      </div>
    </div>
  )
}
